import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { Card } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "../components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Plus, Search, Eye, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../lib/auth";

const EMPTY_FORM = { name: "", mobile: "", email: "", address: "", notes: "" };

export default function Customers() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const canEdit = ["admin", "manager", "billing"].includes(user?.role);
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [debouncedQ, setDebouncedQ] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(q), 250);
    return () => clearTimeout(t);
  }, [q]);

  const load = () => api.get("/customers", { params: { q: debouncedQ } }).then(r => setItems(r.data));
  useEffect(() => { load(); }, [debouncedQ]); // eslint-disable-line

  const openCreate = () => { setEditing(null); setForm(EMPTY_FORM); setOpen(true); };
  const openEdit = (c) => {
    setEditing(c);
    setForm({
      name: c.name || "",
      mobile: c.mobile || c.mobile_masked || "",
      email: c.email || "",
      address: c.address || "",
      notes: c.notes || "",
    });
    setOpen(true);
  };

  const save = async () => {
    if (!form.name || !form.mobile) { toast.error("Name and mobile required"); return; }
    try {
      if (editing) {
        await api.patch(`/customers/${editing.id}`, form);
        toast.success("Customer updated");
      } else {
        await api.post("/customers", form);
        toast.success("Customer created");
      }
      setOpen(false); setEditing(null); setForm(EMPTY_FORM); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const del = async (c) => {
    if (!window.confirm(`Delete customer "${c.name}"?\n\nIf they have invoices/appointments, you'll be asked to confirm cascade deletion (records will be unlinked, not deleted).`)) return;
    try {
      await api.delete(`/customers/${c.id}`);
      toast.success("Customer deleted");
      load();
    } catch (e) {
      const detail = e?.response?.data?.detail || "Failed";
      if (detail.includes("cascade")) {
        if (window.confirm(`${detail}\n\nProceed with cascade delete? (Pets removed; invoices/appointments unlinked but kept for reporting.)`)) {
          try {
            const r = await api.delete(`/customers/${c.id}`, { params: { cascade: true } });
            toast.success(`Deleted — ${r.data.pets_deleted} pet(s) removed, ${r.data.invoices_unlinked} invoice(s) unlinked.`);
            load();
          } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
        }
      } else {
        toast.error(detail);
      }
    }
  };

  return (
    <div className="space-y-5" data-testid="customers-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">CRM</div>
          <h1 className="font-heading text-4xl font-bold tracking-tight mt-1">Customers</h1>
        </div>
        {canEdit && <Button onClick={openCreate} data-testid="add-customer-btn"><Plus className="w-4 h-4 mr-1" />Add customer</Button>}
      </div>

      <Card className="p-4">
        <div className="relative">
          <Search className="absolute left-3 top-2.5 w-4 h-4 text-muted-foreground" />
          <Input placeholder="Search by name or mobile…" value={q} onChange={e => setQ(e.target.value)} className="pl-9" data-testid="customers-search" />
        </div>
        <Table className="mt-3">
          <TableHeader><TableRow><TableHead>Name</TableHead><TableHead>Mobile</TableHead><TableHead>Email</TableHead><TableHead className="text-right"></TableHead></TableRow></TableHeader>
          <TableBody>
            {items.map(c => (
              <TableRow key={c.id} data-testid={`customer-row-${c.id}`}>
                <TableCell className="font-medium">{c.name}</TableCell>
                <TableCell className="font-mono text-sm">{c.mobile_masked || c.mobile}</TableCell>
                <TableCell className="text-sm text-muted-foreground">{c.email || "—"}</TableCell>
                <TableCell className="text-right">
                  <Button asChild variant="ghost" size="sm"><Link to={`/customers/${c.id}`} data-testid={`view-customer-${c.id}`}><Eye className="w-4 h-4 mr-1" />Open</Link></Button>
                  {canEdit && (
                    <Button variant="ghost" size="sm" onClick={() => openEdit(c)} data-testid={`edit-customer-${c.id}`}><Pencil className="w-4 h-4" /></Button>
                  )}
                  {isAdmin && (
                    <Button variant="ghost" size="sm" onClick={() => del(c)} data-testid={`delete-customer-${c.id}`} className="text-destructive hover:text-destructive"><Trash2 className="w-4 h-4" /></Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
            {items.length === 0 && <TableRow><TableCell colSpan={4} className="text-center text-muted-foreground py-8">No customers.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </Card>

      <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setEditing(null); setForm(EMPTY_FORM); } }}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>{editing ? "Edit customer" : "New customer"}</DialogTitle></DialogHeader>
          <div className="space-y-2">
            <div><Label>Name *</Label><Input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} data-testid="customer-name" /></div>
            <div><Label>Mobile *</Label><Input value={form.mobile} onChange={e => setForm(f => ({ ...f, mobile: e.target.value }))} disabled={!!editing} data-testid="customer-mobile" />
              {editing && <div className="text-xs text-muted-foreground mt-1">Mobile cannot be changed (used for OTP login).</div>}
            </div>
            <div><Label>Email</Label><Input value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} /></div>
            <div><Label>Address</Label><Input value={form.address} onChange={e => setForm(f => ({ ...f, address: e.target.value }))} /></div>
            <div><Label>Notes</Label><Input value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} data-testid="save-customer">{editing ? "Save changes" : "Create"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
